import { Popover, Transition } from "@headlessui/react";
import { setMood, setTrend } from "application/features/search/searchSlice";
import { useSelector, useDispatch } from 'react-redux';
import { Fragment, useEffect, useState } from "react";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import ButtonThird from "shared/Button/ButtonThird";
import Checkbox from "shared/Checkbox/Checkbox";
import { useTranslation } from "react-i18next";
import { use } from "i18next";

type MoodTrendType = {
  name: string;
  value: string;
  desc?: string;
}

interface TypeSelectorProps {
  onSelectType: (type: MoodTrendType) => void;
  defaultType: number;
  className?: string;
}

const MoodSelector: React.FC<TypeSelectorProps> = ({ onSelectType, className, defaultType }) => {

  const { t, ready } = useTranslation("translation");

  const initialMood = useSelector((state: any) => state.search.mood);
  const initialTrend = useSelector((state: any) => state.search.trend);

  const [selected, setSelected] = useState<MoodTrendType[]>([]);
  const [selectedTrends, setSelectedTrends] = useState<MoodTrendType[]>([]);
  const dispatch = useDispatch();

  const handleReset = () => {
    setSelected([]);
    setSelectedTrends([]);
    dispatch(setMood([]));
    dispatch(setTrend([]));
  }

  const handleApply = () => {
    dispatch(setMood(selected.map((i) => i.value)));
    dispatch(setTrend(selectedTrends.map((i) => i.value)));
  }


  const moods = t("translation:components.moods", { returnObjects: true }) as {[key in string]: { label: string, description: string }};
  const moodsFormatted: MoodTrendType[] = Object.keys(moods).map((key) => ({
    name: moods[key].label,
    value: key,
    desc: moods[key].description
  }));

  const trends = t("translation:components.trends", { returnObjects: true }) as {[key in string]: { label: string, description: string }};
  const trendsFormatted: MoodTrendType[] = Object.keys(trends).map((key) => ({
    name: trends[key].label,
    value: key,
    desc: trends[key].description
  }));

  useEffect(() => {
    if (initialMood) {
      const selectedMoods = moodsFormatted.filter((mood) => initialMood === mood.value);
      console.log("Selected Moods:", selectedMoods);
      setSelected(selectedMoods);
    }
  }, [initialMood]);

  useEffect(() => {
    if (initialTrend) {
      const selectedTrends = trendsFormatted.filter((trend) => initialTrend.includes(trend.value));
      console.log("Selected Trends:", selectedTrends);
      setSelectedTrends(selectedTrends);
    }
  }, [initialTrend]);

  if (!ready) {
    return null; // or a loading spinner 
  }

  return (
    <div className={`${className} items-center flex justify-center`}>
      <Popover className="relative">
        {({ open, close }) => (
          <>
            <Popover.Button
              className={`flex items-center justify-center text-base xl:text-lg font-semibold rounded-full bg-white py-2 pl-3 pr-2 text-left ${open ? "!border-primary-500 " : ""
                }`}
            >
              <div className="flex flex-col">
                <span>Mood & Trends</span>
                <span className="text-sm text-neutral-400 font-normal">{selected[0]?.name || selectedTrends[0]?.name}</span>
              </div>
              <i className="las la-angle-down ml-2"></i>
            </Popover.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Popover.Panel className="absolute z-10 w-screen max-w-sm px-4 mt-7 left-0 sm:px-0 lg:max-w-md">
                <div className="overflow-hidden rounded-2xl shadow-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700">
                  <div className="relative flex flex-col px-5 py-6 space-y-5">
                    {moodsFormatted.map((item) => (
                      <div key={item.name} className="">
                        <Checkbox
                          name={item.name}
                          label={item.name}
                          isChecked={selected.map(i => i.value).includes(item.value)}
                          subLabel={item.desc}
                          onChange={(e) => {
                            console.log("Mood selected:", item);
                            console.log("Selected moods:", selected);
                            console.log("Event:", e);
                            if (e) {
                              setSelectedTrends([]);
                              setSelected([item]);
                            } else {
                              setSelected(selected.filter((i) => i !== item));
                            }
                          }}
                        />
                      </div>
                    ))}
                    {trendsFormatted.map((item) => (
                      <div key={item.name} className="">
                        <Checkbox
                          name={item.name}
                          label={item.name}
                          isChecked={selectedTrends.map(i => i.value).includes(item.value)}
                          subLabel={item.desc}
                          onChange={(e) => {
                            if (e) {
                              setSelected([]);
                              setSelectedTrends([item]);
                            } else {
                              setSelectedTrends(selectedTrends.filter((i) => i !== item));
                            }
                          }}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="p-5 bg-neutral-50 dark:bg-neutral-900 dark:border-t dark:border-neutral-800 flex items-center justify-between">
                    <Popover.Button>
                      <ButtonThird onClick={handleReset} sizeClass="px-4 py-2 sm:px-5">
                        {t("translation:search.common.reset")}
                      </ButtonThird>
                    </Popover.Button>
                    <Popover.Button>
                      <ButtonPrimary
                        onClick={handleApply}
                        type="button"
                        sizeClass="px-4 py-2 sm:px-5"
                      >
                        {t("translation:search.common.apply")}
                      </ButtonPrimary>

                    </Popover.Button>
                  </div>
                </div>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    </div>

  );
};

export default MoodSelector;
